import React from "react";
import {
    Box,
    Container,
    Typography,
    Paper,
    Alert,
    AlertTitle,
    Link,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";

const PrivacyPolicy = () => {
    const styles = {
        section: {
            marginBottom: 3,
        },
        subheading: {
            marginTop: 2,
            marginBottom: 1,
            color: "#e0e0e0",
        },
        listItem: {
            paddingLeft: 0,
        },
    };

    return (
        <Box sx={{ bgcolor: "#121212", color: "#f5f5f5", py: 4, minHeight: "100vh" }}>
            <Container maxWidth="md">
                <Paper
                    elevation={3}
                    sx={{
                        padding: { xs: 3, md: 5 },
                        backgroundColor: "#1e1e1e",
                        color: "#f5f5f5",
                        border: "1px solid #333",
                    }}
                >
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{ fontWeight: "bold", color: "#98FF98", textAlign: "center" }}
                    >
                        Privacy Policy for Yiggy
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        color="text.secondary"
                        align="center"
                        gutterBottom
                        sx={{ mb: 4, color: "#b0b0b0" }}
                    >
                        Effective Date: June 11, 2025
                    </Typography>

                    <Box sx={styles.section}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            1. Introduction
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Welcome to Yiggy ("we," "us," or "our"). We provide a platform
                            designed to help businesses and their teams ("you," "your,"
                            "User") manage customer interactions on Instagram.
                        </Typography>
                        <Typography variant="body1" paragraph>
                            This Privacy Policy explains how we collect, use, disclose, and
                            safeguard your information when you use our application (the
                            "Platform"). This policy applies to the information we collect
                            from the Users of our Platform (i.e., your team members). It also
                            describes our role and responsibilities concerning the data you
                            process through our Platform, specifically Instagram Direct
                            Messages from your customers and leads ("Client Data").
                        </Typography>
                        <Typography variant="body1" paragraph>
                            By using the Platform, you agree to the collection and use of
                            information in accordance with this policy.
                        </Typography>
                    </Box>

                    <Box sx={styles.section}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            2. Information We Collect
                        </Typography>
                        <Typography variant="body1" paragraph>
                            We collect several different types of information for various
                            purposes to provide and improve our Platform for you.
                        </Typography>

                        <Typography variant="h6" component="h3" sx={styles.subheading}>
                            A. Information You Provide to Us
                        </Typography>
                        <List dense>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary="Account Information"
                                    secondary="When a User registers for an account, we collect personal information such as name, email address, and role within your organization."
                                    primaryTypographyProps={{ fontWeight: "bold" }}
                                    secondaryTypographyProps={{ color: "inherit" }}
                                />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary="Team Associations"
                                    secondary="We store information about which Users belong to which teams within your organization to properly manage permissions and data access."
                                    primaryTypographyProps={{ fontWeight: "bold" }}
                                    secondaryTypographyProps={{ color: "inherit" }}
                                />
                            </ListItem>
                        </List>

                        <Typography variant="h6" component="h3" sx={styles.subheading}>
                            B. Information We Process on Your Behalf (Client Data)
                        </Typography>
                        <List dense>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary="Instagram Conversation Data"
                                    secondary={
                                        <>
                                            Our Platform integrates with the Instagram API to access
                                            and display Direct Messages. This includes message
                                            content, sender/receiver information, timestamps, and
                                            associated metadata. You are the{" "}
                                            <strong>Data Controller</strong> for this Client Data;
                                            Yiggy acts as the <strong>Data Processor</strong> on your
                                            behalf. We only process this data based on your actions
                                            and instructions within the Platform.
                                        </>
                                    }
                                    primaryTypographyProps={{ fontWeight: "bold" }}
                                    secondaryTypographyProps={{ color: "inherit" }}
                                />
                            </ListItem>
                        </List>

                        <Typography variant="h6" component="h3" sx={styles.subheading}>
                            C. Information We Collect Automatically
                        </Typography>
                        <List dense>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary="Usage and Performance Data"
                                    secondary="We automatically track User activity on the Platform. This includes metrics such as messages sent per hour, interactions per hour, login times, and features used. This data is used for performance analytics, productivity tracking, and commission attribution."
                                    primaryTypographyProps={{ fontWeight: "bold" }}
                                    secondaryTypographyProps={{ color: "inherit" }}
                                />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary="Log and Device Data"
                                    secondary="We collect information that your browser sends whenever you visit our Platform. This may include your computer's Internet Protocol (IP) address, browser type, browser version, the pages of our Platform that you visit, the time and date of your visit, and other diagnostic data."
                                    primaryTypographyProps={{ fontWeight: "bold" }}
                                    secondaryTypographyProps={{ color: "inherit" }}
                                />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary="Cookies and Local Storage"
                                    secondary="We use cookies and similar tracking technologies like browser local storage to track activity on our Platform and hold certain information. This helps us maintain your session and cache frequently accessed data to improve performance. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent."
                                    primaryTypographyProps={{ fontWeight: "bold" }}
                                    secondaryTypographyProps={{ color: "inherit" }}
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Box sx={styles.section}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            3. How We Use Your Information
                        </Typography>
                        <Typography variant="body1" paragraph>
                            We use the collected information for the following purposes:
                        </Typography>
                        <List dense>
                            <ListItem sx={styles.listItem}>
                                <ListItemText secondary="To Provide and Maintain our Platform: To manage user accounts, provide access to the DM management interface, and ensure the Platform is functioning correctly." />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText secondary="To Manage Your Team: To track user engagement, assign conversations, prevent duplicate outreach, and provide performance analytics for internal accountability and commission calculations." />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText secondary="To Improve Our Platform: To understand how Users interact with our Platform so we can enhance the user experience, optimize performance, and develop new features." />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText secondary="For Security: To monitor for and prevent fraudulent or unauthorized activity." />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText secondary="To Fulfill Legal Obligations: To comply with applicable legal requirements, such as responding to lawful requests from public authorities." />
                            </ListItem>
                        </List>
                    </Box>

                    <Box sx={styles.section}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            4. How We Share and Disclose Information
                        </Typography>
                        <Typography variant="body1" paragraph>
                            We do not sell your personal information. We may share information
                            in the following circumstances:
                        </Typography>
                        <List dense>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary="Within Your Organization"
                                    secondary="User information and activity are visible to authorized members of your team, as configured in your account settings."
                                    primaryTypographyProps={{ fontWeight: "bold" }}
                                    secondaryTypographyProps={{ color: "inherit" }}
                                />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary="Third-Party Service Providers"
                                    secondary={
                                        <>
                                            We use third-party companies to facilitate our Platform
                                            ("Service Providers"). These include: Hosting (Render,
                                            Vercel) and Database (MongoDB). These third parties have
                                            access to your information only to perform these tasks on
                                            our behalf and are obligated not to disclose or use it for
                                            any other purpose.
                                        </>
                                    }
                                    primaryTypographyProps={{ fontWeight: "bold" }}
                                    secondaryTypographyProps={{ color: "inherit" }}
                                />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary="Business Transfers"
                                    secondary="If we are involved in a merger, acquisition, or asset sale, your Personal Information may be transferred."
                                    primaryTypographyProps={{ fontWeight: "bold" }}
                                    secondaryTypographyProps={{ color: "inherit" }}
                                />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary="Legal Requirements"
                                    secondary="We may disclose your information if required to do so by law or in response to valid requests by public authorities."
                                    primaryTypographyProps={{ fontWeight: "bold" }}
                                    secondaryTypographyProps={{ color: "inherit" }}
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Box sx={styles.section}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            5. Data Storage and Security
                        </Typography>
                        <Typography variant="body1" paragraph>
                            The security of your data is a priority. We use commercially
                            acceptable means to protect your Personal Information and Client
                            Data, including secure cloud infrastructure and optimized APIs.
                            However, please remember that no method of transmission over the
                            Internet or method of electronic storage is 100% secure.
                        </Typography>
                    </Box>

                    <Box sx={styles.section}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            6. Data Retention
                        </Typography>
                        <Typography variant="body1" paragraph>
                            We will retain your Personal Information and Usage Data only for
                            as long as is necessary for the purposes set out in this Privacy
                            Policy, such as maintaining your active account. We will retain
                            Client Data according to your instructions or as long as your
                            account is active. We will also retain and use your information to
                            the extent necessary to comply with our legal obligations, resolve
                            disputes, and enforce our agreements.
                        </Typography>
                    </Box>

                    <Box sx={styles.section}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            7. Your Data Protection Rights
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Depending on your location, you may have the following rights
                            regarding your personal information:
                        </Typography>
                        <List dense>
                            <ListItem sx={styles.listItem}>
                                <ListItemText secondary="The right to access, update, or delete the information we have on you." />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText secondary="The right of rectification." />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText secondary="The right to object to processing." />
                            </ListItem>
                            <ListItem sx={styles.listItem}>
                                <ListItemText secondary="The right of restriction." />
                            </ListItem>
                        </List>
                        <Typography variant="body1" paragraph>
                            Please note that these rights apply to the information of the
                            Users of the Yiggy platform. For requests related to{" "}
                            <strong>Client Data</strong> (your Instagram contacts), you, as
                            the Data Controller, are responsible for honoring those requests.
                            We will assist you as needed.
                        </Typography>
                    </Box>

                    <Box sx={styles.section}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            8. Third-Party Services
                        </Typography>
                        <Typography variant="body1" paragraph>
                            Our Platform's functionality relies on the Instagram API. Your use
                            of the Instagram integration is subject to Instagram's own terms
                            of service and privacy policy, which we encourage you to review.
                        </Typography>
                    </Box>

                    <Box sx={styles.section}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            9. Changes to This Privacy Policy
                        </Typography>
                        <Typography variant="body1" paragraph>
                            We may update our Privacy Policy from time to time. We will notify
                            you of any changes by posting the new Privacy Policy on this page
                            and updating the "Effective Date" at the top.
                        </Typography>
                    </Box>

                    <Box sx={styles.section}>
                        <Typography variant="h5" component="h2" gutterBottom>
                            10. Contact Us
                        </Typography>
                        <Typography variant="body1" paragraph>
                            If you have any questions about this Privacy Policy, please
                            contact us:
                        </Typography>
                        <List dense>
                            <ListItem sx={styles.listItem}>
                                <ListItemText
                                    primary={
                                        <>
                                            <strong>Email:</strong>{" "}
                                            <Link
                                                href="mailto:yxn@yiggy.net"
                                                sx={{ color: "#98FF98", wordBreak: "break-all" }}
                                            >
                                                yxn@yiggy.net
                                            </Link>
                                        </>
                                    }
                                />
                            </ListItem>
                        </List>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default PrivacyPolicy;